import { formatCompact, formatUtc } from './chartUtils'
import type { RulerGeometry, RulerSelection } from './useChartRuler'

interface RulerOverlayProps {
  geometry: RulerGeometry
  selection: RulerSelection
  color: string
  summary: string
  detail: string
  metric: 'price' | 'oi'
  interval: string
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, Math.max(min, max)))

/** Separate nearby labels while keeping their ticks at the exact selected values. */
function labelPositions(values: number[], size: number, min: number, max: number): number[] {
  const positions = values.map((value) => clamp(value - size / 2, min, max - size))
  if (positions.length === 2) {
    const first = values[0] <= values[1] ? 0 : 1
    const second = 1 - first
    if (positions[second] < positions[first] + size + 2) {
      positions[first] = clamp((positions[first] + positions[second] - size - 2) / 2, min, max - size * 2 - 2)
      positions[second] = positions[first] + size + 2
    }
  }
  return positions
}

export function RulerOverlay({ geometry, selection, color, summary, detail, metric, interval }: RulerOverlayProps) {
  const { start, end, startValue, endValue, plot, width, height } = geometry
  const right = plot.x + plot.width
  const bottom = plot.y + plot.height
  const left = Math.min(start.x, end.x)
  const top = Math.min(start.y, end.y)
  const centerX = (start.x + end.x) / 2
  const centerY = (start.y + end.y) / 2
  const x1 = clamp(left, plot.x, right)
  const x2 = clamp(Math.max(start.x, end.x), plot.x, right)
  const y1 = clamp(top, plot.y, bottom)
  const y2 = clamp(Math.max(start.y, end.y), plot.y, bottom)
  const intersectsX = Math.max(start.x, end.x) >= plot.x && left <= right
  const intersectsY = Math.max(start.y, end.y) >= plot.y && top <= bottom
  const axisWidth = Math.max(0, width - right - 2)
  const timeHeight = 34
  // Upper stacked panels hide ordinary time ticks; keep ruler times visible there too.
  const timeY = Math.min(bottom + 3, height - timeHeight - 2)
  const timeWidth = Math.min(88, Math.max(1, (plot.width - 4) / 2))
  const endpoints = [
    { key: 'start', point: start, value: startValue, timestamp: selection.startTimestamp },
    { key: 'end', point: end, value: endValue, timestamp: selection.endTimestamp },
  ]
  const valueLabels = endpoints.filter((entry, index) => entry.point.y >= plot.y && entry.point.y <= bottom
    && (index === 0 || entry.value !== startValue))
  const timeLabels = endpoints.filter((entry, index) => entry.point.x >= plot.x && entry.point.x <= right
    && (index === 0 || entry.timestamp !== selection.startTimestamp))
  const valueHeight = Math.min(20, Math.max(1, (plot.height - 2) / 2))
  const valuePositions = labelPositions(valueLabels.map((entry) => entry.point.y), valueHeight, plot.y, bottom)
  const timePositions = labelPositions(timeLabels.map((entry) => entry.point.x), timeWidth, plot.x, right)
  const summaryWidth = Math.min(plot.width, Math.max(150, Math.max(summary.length * 7, detail.length * 6) + 20))
  const summaryX = clamp(centerX - summaryWidth / 2, plot.x, right - summaryWidth)
  const summaryBottom = Math.min(bottom, timeY)
  const summaryHeight = Math.min(44, Math.max(20, summaryBottom - plot.y - 4))
  const summaryY = clamp(top - summaryHeight - 6, plot.y + 2, summaryBottom - summaryHeight - 2)

  return <svg
    className="pointer-events-none absolute left-0 top-0 z-20 overflow-hidden"
    width={width} height={height} aria-hidden="true" data-testid="screener-ruler-overlay"
    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
  >
    <svg x={plot.x} y={plot.y} width={plot.width} height={plot.height}
      viewBox={`${plot.x} ${plot.y} ${plot.width} ${plot.height}`} overflow="hidden" data-testid="ruler-plot-clip">
      <rect x={left} y={top} width={Math.max(1, Math.abs(end.x - start.x))} height={Math.max(1, Math.abs(end.y - start.y))}
        fill={color} fillOpacity={0.16} stroke={color} strokeOpacity={0.65} />
      <path d={`M ${start.x} ${centerY} H ${end.x} M ${centerX} ${start.y} V ${end.y}`} stroke={color} strokeWidth={1.3} />
      <path d={`M ${end.x - Math.sign(end.x - start.x) * 6} ${centerY - 4} L ${end.x} ${centerY} L ${end.x - Math.sign(end.x - start.x) * 6} ${centerY + 4}`} fill="none" stroke={color} />
      <circle cx={start.x} cy={start.y} r={3} fill={color} />
      <circle cx={end.x} cy={end.y} r={3} fill={color} />
      {intersectsX && intersectsY && <g data-testid="ruler-summary" transform={`translate(${summaryX}, ${summaryY})`}>
        <rect width={summaryWidth} height={summaryHeight} rx={5} fill={color} />
        <text x={summaryWidth / 2} y={summaryHeight >= 30 ? 15 : summaryHeight / 2 + 4} textAnchor="middle" fill="#07111f" fontSize={12} fontWeight={600}>{summary}</text>
        {summaryHeight >= 30 && <text x={summaryWidth / 2} y={summaryHeight - 9} textAnchor="middle" fill="#07111f" fontSize={10}>{detail}</text>}
      </g>}
    </svg>
    {intersectsY && axisWidth > 0 && <g data-testid="ruler-value-axis">
      <rect data-testid="ruler-value-band" x={right} y={y1} width={axisWidth} height={Math.max(1, y2 - y1)} fill={color} fillOpacity={0.2} />
      {valueLabels.map((entry, index) => {
        const text = metric === 'price'
          ? entry.value.toLocaleString(undefined, { maximumFractionDigits: 8 })
          : formatCompact(entry.value, 3)
        return <g key={entry.key} data-testid={`ruler-value-${entry.key}`} transform={`translate(${right}, ${valuePositions[index]})`}>
          <title>{`${entry.value.toLocaleString(undefined, { maximumFractionDigits: 8 })} ${metric === 'price' ? 'USDT' : 'coins'}`}</title>
          <path d={`M 0 ${entry.point.y - valuePositions[index]} H 4 L 7 ${valueHeight / 2}`} fill="none" stroke={color} />
          <rect x={4} width={Math.max(0, axisWidth - 4)} height={valueHeight} rx={2} fill={color} />
          <text x={axisWidth / 2 + 2} y={valueHeight / 2} dominantBaseline="central" textAnchor="middle" fill="#07111f"
            fontSize={Math.min(10, Math.max(1, (axisWidth - 12) / (text.length * 0.62)))}>{text}</text>
        </g>
      })}
    </g>}
    {intersectsX && <g data-testid="ruler-time-axis">
      <rect data-testid="ruler-time-band" x={x1} y={timeY} width={Math.max(1, x2 - x1)} height={timeHeight} fill={color} fillOpacity={0.2} />
      {timeLabels.map((entry, index) => {
        const date = new Date(entry.timestamp * 1000)
        const day = `${date.getUTCDate().toString().padStart(2, '0')} ${date.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })} '${date.getUTCFullYear().toString().slice(-2)}`
        const time = date.toISOString().slice(11, interval === '5s' ? 19 : 16)
        return <g key={entry.key} data-testid={`ruler-time-${entry.key}`} transform={`translate(${timePositions[index]}, ${timeY})`}>
          <title>{formatUtc(entry.timestamp * 1000, interval === '5s')}</title>
          <path d={`M ${entry.point.x - timePositions[index]} -3 V 0`} stroke={color} />
          <rect width={timeWidth} height={timeHeight} rx={2} fill={color} />
          <text x={timeWidth / 2} y={13} textAnchor="middle" fill="#07111f" fontSize={Math.min(10, timeWidth / 6.8)}>{day}</text>
          <text x={timeWidth / 2} y={27} textAnchor="middle" fill="#07111f" fontSize={10}>{time}</text>
        </g>
      })}
    </g>}
  </svg>
}
